// src/marketing/ResourcesPage.js - Complete Resources Hub
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Download,
  FileText,
  Video,
  Users,
  ArrowRight,
  Calendar,
  Clock,
  Star,
  Search,
  Filter,
  ExternalLink,
  Play,
  CheckCircle,
} from 'lucide-react';

const ResourcesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState('All');

  // Resource categories
  const categories = [
    'All',
    'Getting Started',
    'Google Sheets',
    'Customization',
    'Business Growth',
    'Technical',
    'Industry Specific',
  ];

  const resourceTypes = [
    'All',
    'Guide',
    'Template',
    'Video',
    'Blog Post',
    'Case Study',
    'Webinar',
  ];

  // Featured resources
  const featuredResources = [
    {
      id: 1,
      title: 'Complete Setup Guide: From Google Sheets to Client Portal',
      description:
        'Step-by-step walkthrough to get your first client portal live in under 30 minutes.',
      type: 'Guide',
      category: 'Getting Started',
      readTime: '15 min',
      downloadCount: '2.4k',
      featured: true,
      image: 'https://placehold.co/400x240?text=Setup+Guide',
      link: '/resources/guides/complete-setup',
    },
    {
      id: 2,
      title: 'Tax Season Client Portal Template',
      description:
        'Ready-to-use portal configuration for tax preparers with status workflows and branding.',
      type: 'Template',
      category: 'Industry Specific',
      readTime: '5 min setup',
      downloadCount: '1.8k',
      featured: true,
      image: 'https://placehold.co/400x240?text=Tax+Template',
      link: '/resources/templates/tax-season',
    },
    {
      id: 3,
      title: 'How Wilson CPA Reduced Status Calls by 80%',
      description:
        'Real case study showing how a 15-person firm transformed their client communication.',
      type: 'Case Study',
      category: 'Business Growth',
      readTime: '8 min',
      downloadCount: '956',
      featured: true,
      image: 'https://placehold.co/400x240?text=Case+Study',
      link: '/resources/case-studies/wilson-cpa',
    },
  ];

  // All resources
  const allResources = [
    // Getting Started Guides
    {
      id: 4,
      title: 'Your First 24 Hours with TrackerSheets',
      description:
        'Essential tasks to complete when you first sign up for maximum impact.',
      type: 'Guide',
      category: 'Getting Started',
      readTime: '10 min',
      publishDate: '2025-05-15',
      author: 'TrackerSheets Team',
      tags: ['beginner', 'setup', 'onboarding'],
      link: '/resources/guides/first-24-hours',
    },
    {
      id: 5,
      title: 'Understanding Client Portal URLs and Sharing',
      description:
        'Best practices for sharing portal links and managing client access.',
      type: 'Guide',
      category: 'Getting Started',
      readTime: '5 min',
      publishDate: '2025-05-10',
      author: 'Sarah Chen',
      tags: ['sharing', 'urls', 'access'],
      link: '/resources/guides/portal-sharing',
    },

    // Google Sheets Resources
    {
      id: 6,
      title: 'Google Sheets Formatting for Better Portals',
      description:
        'How to structure your spreadsheet data for optimal portal display.',
      type: 'Guide',
      category: 'Google Sheets',
      readTime: '12 min',
      publishDate: '2025-05-08',
      author: 'Mike Rodriguez',
      tags: ['formatting', 'data', 'optimization'],
      link: '/resources/guides/sheets-formatting',
    },
    {
      id: 7,
      title: 'Advanced Google Sheets Formulas for Status Tracking',
      description:
        'Power-user formulas to automate status updates and calculations.',
      type: 'Guide',
      category: 'Google Sheets',
      readTime: '18 min',
      publishDate: '2025-05-05',
      author: 'Data Team',
      tags: ['formulas', 'automation', 'advanced'],
      link: '/resources/guides/advanced-formulas',
    },
    {
      id: 8,
      title: 'Client Data Import Template',
      description:
        'Pre-formatted Google Sheet template for importing existing client data.',
      type: 'Template',
      category: 'Google Sheets',
      readTime: '2 min setup',
      publishDate: '2025-05-01',
      author: 'TrackerSheets Team',
      tags: ['template', 'import', 'data'],
      link: '/resources/templates/data-import',
    },

    // Customization Resources
    {
      id: 9,
      title: 'Branding Your Client Portal Like a Pro',
      description:
        'Design tips and best practices for professional portal branding.',
      type: 'Guide',
      category: 'Customization',
      readTime: '15 min',
      publishDate: '2025-04-28',
      author: 'Design Team',
      tags: ['branding', 'design', 'professional'],
      link: '/resources/guides/professional-branding',
    },
    {
      id: 10,
      title: 'Custom Status Workflow Examples',
      description:
        'Real-world examples of status workflows for different industries.',
      type: 'Guide',
      category: 'Customization',
      readTime: '10 min',
      publishDate: '2025-04-25',
      author: 'Industry Team',
      tags: ['workflows', 'examples', 'industries'],
      link: '/resources/guides/status-workflows',
    },

    // Business Growth
    {
      id: 11,
      title: 'Using Client Portals to Increase Referrals',
      description:
        'How professional portals improve client satisfaction and drive referrals.',
      type: 'Blog Post',
      category: 'Business Growth',
      readTime: '7 min',
      publishDate: '2025-04-20',
      author: 'Business Team',
      tags: ['referrals', 'growth', 'satisfaction'],
      link: '/resources/blog/increase-referrals',
    },
    {
      id: 12,
      title: 'ROI Calculator: Measuring Portal Impact',
      description:
        'Calculate the time and cost savings from implementing client portals.',
      type: 'Template',
      category: 'Business Growth',
      readTime: '5 min',
      publishDate: '2025-04-15',
      author: 'Analytics Team',
      tags: ['roi', 'calculator', 'measurement'],
      link: '/resources/templates/roi-calculator',
    },

    // Industry Specific
    {
      id: 13,
      title: 'Legal Firms: Case Status Tracking Best Practices',
      description:
        'Specialized guide for law firms implementing client case tracking.',
      type: 'Guide',
      category: 'Industry Specific',
      readTime: '12 min',
      publishDate: '2025-04-10',
      author: 'Legal Team',
      tags: ['legal', 'cases', 'law firms'],
      link: '/resources/guides/legal-case-tracking',
    },
    {
      id: 14,
      title: 'Construction Project Portal Template',
      description:
        'Ready-to-use template for construction companies tracking project progress.',
      type: 'Template',
      category: 'Industry Specific',
      readTime: '3 min setup',
      publishDate: '2025-04-05',
      author: 'Industry Team',
      tags: ['construction', 'projects', 'template'],
      link: '/resources/templates/construction-projects',
    },

    // Video Resources
    {
      id: 15,
      title: 'Portal Setup Walkthrough (Video)',
      description:
        'Watch our founder set up a complete client portal from scratch.',
      type: 'Video',
      category: 'Getting Started',
      readTime: '22 min',
      publishDate: '2025-03-30',
      author: 'TrackerSheets Team',
      tags: ['video', 'walkthrough', 'setup'],
      link: '/resources/videos/setup-walkthrough',
    },
    {
      id: 16,
      title: 'Advanced Customization Webinar',
      description:
        'Live webinar covering advanced portal customization techniques.',
      type: 'Webinar',
      category: 'Customization',
      readTime: '45 min',
      publishDate: '2025-03-25',
      author: 'Product Team',
      tags: ['webinar', 'advanced', 'customization'],
      link: '/resources/webinars/advanced-customization',
    },
  ];

  // Combine featured and regular resources
  const combinedResources = [...featuredResources, ...allResources];

  // Filter resources
  const filteredResources = combinedResources.filter((resource) => {
    const matchesSearch =
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (resource.tags &&
        resource.tags.some((tag) =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        ));

    const matchesCategory =
      selectedCategory === 'All' || resource.category === selectedCategory;
    const matchesType =
      selectedType === 'All' || resource.type === selectedType;

    return matchesSearch && matchesCategory && matchesType;
  });

  // Get featured resources that match filters
  const filteredFeatured = filteredResources.filter((r) => r.featured);
  const filteredRegular = filteredResources.filter((r) => !r.featured);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Guide':
        return <BookOpen className="h-5 w-5" />;
      case 'Template':
        return <Download className="h-5 w-5" />;
      case 'Video':
        return <Video className="h-5 w-5" />;
      case 'Blog Post':
        return <FileText className="h-5 w-5" />;
      case 'Case Study':
        return <Users className="h-5 w-5" />;
      case 'Webinar':
        return <Calendar className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Guide':
        return 'bg-blue-100 text-blue-800';
      case 'Template':
        return 'bg-green-100 text-green-800';
      case 'Video':
        return 'bg-purple-100 text-purple-800';
      case 'Blog Post':
        return 'bg-orange-100 text-orange-800';
      case 'Case Study':
        return 'bg-indigo-100 text-indigo-800';
      case 'Webinar':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      {/* Hero Section */}
      <header className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
            Resources & Learning Center
          </h1>
          <p className="mt-5 max-w-3xl mx-auto text-xl text-gray-600">
            Everything you need to master TrackerSheets and transform your
            client communication. Guides, templates, case studies, and expert
            tips.
          </p>
        </div>
      </header>

      {/* Search and Filters */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
              {/* Search */}
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search guides, templates, videos..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <select
                    className="border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <select
                  className="border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  {resourceTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Resources */}
      {filteredFeatured.length > 0 && (
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center mb-8">
              <Star className="h-6 w-6 text-yellow-500 mr-2" />
              <h2 className="text-2xl font-bold text-gray-900">
                Featured Resources
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredFeatured.map((resource) => (
                <div
                  key={resource.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={resource.image}
                      alt={resource.title}
                      className="w-full h-48 object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(
                          resource.type
                        )}`}
                      >
                        {getTypeIcon(resource.type)}
                        <span className="ml-1">{resource.type}</span>
                      </span>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        {resource.readTime}
                      </div>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {resource.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {resource.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {resource.downloadCount &&
                          `${resource.downloadCount} downloads`}
                      </span>
                      <Link
                        to={resource.link}
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        {resource.type === 'Video' ||
                        resource.type === 'Webinar'
                          ? 'Watch'
                          : resource.type === 'Template'
                          ? 'Download'
                          : 'Read'}
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Resources */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            All Resources
            {searchTerm && ` (${filteredRegular.length} results)`}
          </h2>

          {filteredRegular.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No resources found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search terms or filters.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredRegular.map((resource) => (
                <div
                  key={resource.id}
                  className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow p-6"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(
                            resource.type
                          )} mr-3`}
                        >
                          {getTypeIcon(resource.type)}
                          <span className="ml-1">{resource.type}</span>
                        </span>
                        <span className="text-xs text-gray-500">
                          {resource.category}
                        </span>
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {resource.title}
                      </h3>
                      <p className="text-gray-600 mb-3">
                        {resource.description}
                      </p>

                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        {resource.readTime && (
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {resource.readTime}
                          </div>
                        )}
                        {resource.author && <span>By {resource.author}</span>}
                        {resource.publishDate && (
                          <span>
                            {new Date(
                              resource.publishDate
                            ).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      {resource.tags && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {resource.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-700"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="mt-4 sm:mt-0 sm:ml-6 flex-shrink-0">
                      <Link
                        to={resource.link}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                      >
                        {resource.type === 'Video' ||
                        resource.type === 'Webinar' ? (
                          <>
                            <Play className="mr-2 h-4 w-4" />
                            Watch
                          </>
                        ) : resource.type === 'Template' ? (
                          <>
                            <Download className="mr-2 h-4 w-4" />
                            Download
                          </>
                        ) : (
                          <>
                            <BookOpen className="mr-2 h-4 w-4" />
                            Read
                          </>
                        )}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Popular Resource Categories
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Jump directly to the resources you need most
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-blue-50 rounded-lg p-6 hover:bg-blue-100 transition-colors">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 bg-blue-600 rounded-md p-3">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Getting Started
                  </h3>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                New to TrackerSheets? Start here with our comprehensive
                onboarding guides.
              </p>
              <button
                onClick={() => setSelectedCategory('Getting Started')}
                className="text-blue-600 font-medium hover:text-blue-800"
              >
                View guides →
              </button>
            </div>

            <div className="bg-green-50 rounded-lg p-6 hover:bg-green-100 transition-colors">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 bg-green-600 rounded-md p-3">
                  <Download className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Free Templates
                  </h3>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Ready-to-use templates for different industries and use cases.
              </p>
              <button
                onClick={() => setSelectedType('Template')}
                className="text-green-600 font-medium hover:text-green-800"
              >
                Browse templates →
              </button>
            </div>

            <div className="bg-purple-50 rounded-lg p-6 hover:bg-purple-100 transition-colors">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 bg-purple-600 rounded-md p-3">
                  <Video className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Video Tutorials
                  </h3>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Watch step-by-step tutorials and webinars from our experts.
              </p>
              <button
                onClick={() => setSelectedType('Video')}
                className="text-purple-600 font-medium hover:text-purple-800"
              >
                Watch videos →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
            Need More Help?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Can't find what you're looking for? Our support team is here to
            help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Contact Support
            </Link>
            <Link
              to="/help"
              className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <ExternalLink className="mr-2 h-5 w-5" />
              Knowledge Base
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
